import {LatLngBoundsExpression} from 'leaflet';

export type PetSpecies = 'cat' | 'dog' | 'rabbit' | 'bird' | 'fish' | 'hamster' | 'reptile' | 'other';

export interface Pet {
  id: string;
  employeeId: string;
  name: string;
  species: PetSpecies | string;
  role?: string;
  bio: string;
  photoUrl?: string;
}

export interface Team {
  id: string;
  name: string;
  iconType: 'lucide' | 'emoji' | 'image';
  iconValue: string;
}

export interface TemporaryLocation {
  id: string;
  employeeId: string;
  city: string;
  country: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  timezone: string;
  label: string;
  startDate: string;
  endDate: string;
}

export interface Profile {
  id: string;
  name: string;
  avatar?: string;
  avatarHover?: string;
  role: string;
  department: string;
  employer: string;
  email: string;
  phone?: string;
  bio: string;
  startDate: string;
  skills: string[];
  languages: string[];
  location: {
    city: string;
    country: string;
    coordinates: {
      latitude: number;
      longitude: number;
    };
    timezone: string;
    coordinatesManual: boolean;
  };
  githubHandle?: string;
  newHireDeckUrl?: string;
  socialMedia?: {
    linkedin?: string;
    twitter?: string;
    github?: string;
    slack?: string;
  };
}

export interface Employee extends Profile {
  avatar: string;
  avatarHover: string;
  temporaryLocations?: TemporaryLocation[];
  teams?: Team[];
  pets?: Pet[];
  meetColleagueAvailable?: boolean;
  meetColleagueUnavailableUntil?: string;
}

type Degrees = number;

type RectBounds = {
  minLat: Degrees;
  minLng: Degrees;
  maxLat: Degrees;
  maxLng: Degrees;
}

export class EmployeeCluster {
  center: {
    latitude: Degrees;
    longitude: Degrees;
  };
  employees: Set<Employee>;
  /** Bounding box inflated by avatarSize/2 — used for overlap detection */
  bbox: RectBounds;
  /** Tight bounding box covering only the actual member coordinates */
  memberBbox: RectBounds;
  avatarSize: Degrees;
  root: Employee;
  // Running coordinate sums so the centroid updates in O(1) per add.
  private sumLat: Degrees;
  private sumLng: Degrees;

  constructor(employee: Employee, avatarSize: Degrees) {
    this.root = employee;
    this.center = employee.location.coordinates;
    this.employees = new Set([employee]);
    const { latitude: lat, longitude: lng } = employee.location.coordinates;
    this.sumLat = lat;
    this.sumLng = lng;
    this.bbox = {
      minLat: lat - avatarSize / 2,
      maxLat: lat + avatarSize / 2,
      minLng: lng - avatarSize / 2,
      maxLng: lng + avatarSize / 2,
    };
    this.memberBbox = { minLat: lat, maxLat: lat, minLng: lng, maxLng: lng };
    this.avatarSize = avatarSize;
  }

  size(): number {
    return this.employees.size;
  }

  rootId(): string {
    return this.root.id;
  }

  bounds(): LatLngBoundsExpression {
    return [[this.bbox.minLat, this.bbox.minLng], [this.bbox.maxLat, this.bbox.maxLng]];
  }

  memberBounds(): LatLngBoundsExpression {
    return [
      [this.memberBbox.minLat, this.memberBbox.minLng],
      [this.memberBbox.maxLat, this.memberBbox.maxLng],
    ];
  }

  addEmployee(employee: Employee) {
    if (this.employees.has(employee)) {
      return;
    }
    this.employees.add(employee);

    const { latitude: lat, longitude: lng } = employee.location.coordinates;

    this.sumLat += lat;
    this.sumLng += lng;
    this.center = { latitude: this.sumLat / this.employees.size, longitude: this.sumLng / this.employees.size };

    if (lat + this.avatarSize / 2 > this.bbox.maxLat) {
      this.bbox.maxLat = lat + this.avatarSize / 2;
    } else if (lat - this.avatarSize / 2 < this.bbox.minLat) {
      this.bbox.minLat = lat - this.avatarSize / 2;
    }

    if (lng + this.avatarSize / 2 > this.bbox.maxLng) {
      this.bbox.maxLng = lng + this.avatarSize / 2;
    } else if (lng - this.avatarSize / 2 < this.bbox.minLng) {
      this.bbox.minLng = lng - this.avatarSize / 2;
    }

    if (lat < this.memberBbox.minLat) this.memberBbox.minLat = lat;
    if (lat > this.memberBbox.maxLat) this.memberBbox.maxLat = lat;
    if (lng < this.memberBbox.minLng) this.memberBbox.minLng = lng;
    if (lng > this.memberBbox.maxLng) this.memberBbox.maxLng = lng;
  }

  join(otherCluster: EmployeeCluster) {
    for (const employee of otherCluster.employees) {
      this.addEmployee(employee);
    }
  }
}
